import { Send, Sparkles, Crown, Briefcase, Clock } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, Steps, StatusBadge, RoleBadge, Callout, pathFor,
  Diagram, MockTabs, MockTable, MockButton,
} from "../shared";

export default function UpwardDispatchSection() {
  return (
    <div data-testid="guide-upward-dispatch">
      <PageHeading
        icon={Send}
        title="Upward Dispatch"
        description="A step added in v2.5.0 for items heading above the SDF MD. After the MD approves, the item waits in 'Awaiting Dispatch' until the PMO or Board Secretary marks it sent — and the system quietly times how long that took."
      />

      <Callout variant="info" title="In plain words">
        Some items need to be passed up to someone senior (the EDGE Chairman, the Board, and so on). After the MD says "yes", there's a short waiting room called <span className="text-white">Awaiting Dispatch</span> where the item sits until someone actually sends it up and records that they did. This makes a previously invisible step visible.
      </Callout>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          If you're the PMO or Board Secretary, this is your job: clear the Awaiting Dispatch waiting room promptly. For everyone else, this page explains where above-MD items go after the MD approves them.
        </p>
      </Section>

      <Section title="What changed in v2.5.0">
        <div className="flex items-start gap-3 mb-3">
          <Sparkles className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
          <p className="text-sm text-white/65 leading-relaxed">
            Before, when the SDF MD signed off an EDGE Chairman or Board / ExCom item, it jumped straight to Closed. The actual sending-upward was done off-system with no record. Now there's a tracked step in between, with a working-day target and a performance panel.
          </p>
        </div>
        <ul className="list-disc list-inside text-xs text-white/55 space-y-1.5 ml-2">
          <li>A new status: <StatusBadge status="AWAITING_DISPATCH" />. Items land here automatically when the SDF MD approves an above-MD item.</li>
          <li>A new tab on the MD Meetings page — <span className="text-white">Awaiting Dispatch</span> — sorted oldest-approved-first.</li>
          <li>A new action — <Link href={pathFor("forms/mark-dispatched")} className="text-cyan-300 hover:underline">Mark Dispatched</Link> — recording recipient, date sent, and an optional note, then moving the item to <StatusBadge status="SENT_UPWARD" />.</li>
          <li>A new history entry — <span className="text-white">DISPATCHED_UPWARD</span> — with the recipient, date, and how many working days it took.</li>
          <li>A new card on the Summary page — <span className="text-white">Upward Dispatch Performance</span> — showing the average time and how many are overdue.</li>
          <li>The Approve &amp; Close button now does sign-off and close together in one action when sign-off is required.</li>
        </ul>
      </Section>

      <Section title="The Awaiting Dispatch waiting room">
        <Diagram
          title="MD Meetings → Awaiting Dispatch"
          frameLabel="MD Meetings"
          legend={[
            <>Click the <span className="text-white">Awaiting Dispatch</span> tab. Items are sorted oldest-approved-first.</>,
            <>Each row shows a coloured held-time chip: <span className="text-emerald-300">green</span> on target, <span className="text-amber-300">amber</span> slightly late, <span className="text-red-300">red</span> well over.</>,
            <>Click <span className="text-white">Mark Dispatched</span> on a row to record who you sent it to and when.</>,
          ]}
        >
          <MockTabs
            tabs={[
              { label: "Review Queue" },
              { label: "Awaiting Dispatch", active: true, marker: 1 },
            ]}
          />
          <MockTable
            columns={["Item", "Lane", "Held (working days)", "Action"]}
            rows={[
              ["Board resolution 14", "Board / ExCom", <span className="text-red-300">● 6 (Red)</span>, <MockButton label="Mark Dispatched" marker={3} variant="ghost" />],
              ["EDGE paper", "EDGE Chairman", <span className="text-emerald-300">● 1 (Green)</span>, <MockButton label="Mark Dispatched" variant="ghost" />],
            ]}
          />
          <p className="text-2xs text-white/45 flex items-center gap-1.5">
            <span className="inline-flex w-4 h-4 rounded-full bg-cyan-500/30 text-cyan-100 text-[10px] font-bold items-center justify-center ring-1 ring-cyan-400/50">2</span>
            The coloured chip tells you at a glance how long each item has waited.
          </p>
        </Diagram>
      </Section>

      <Section title="Which lanes go through Awaiting Dispatch">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="p-3 rounded-md bg-white/[0.02]" data-testid="upward-lane-edge-chairman">
            <div className="flex items-center gap-2 mb-1">
              <Crown className="w-4 h-4 text-purple-400" />
              <p className="text-sm text-white font-medium">EDGE Chairman</p>
            </div>
            <p className="text-xs text-white/55">Items addressed to or sponsored by the EDGE Chairman.</p>
          </div>
          <div className="p-3 rounded-md bg-white/[0.02]" data-testid="upward-lane-edge-md">
            <div className="flex items-center gap-2 mb-1">
              <Briefcase className="w-4 h-4 text-blue-400" />
              <p className="text-sm text-white font-medium">EDGE MD Office</p>
            </div>
            <p className="text-xs text-white/55">Items routed through the EDGE Managing Director's office.</p>
          </div>
          <div className="p-3 rounded-md bg-white/[0.02]" data-testid="upward-lane-board">
            <div className="flex items-center gap-2 mb-1">
              <Crown className="w-4 h-4 text-emerald-400" />
              <p className="text-sm text-white font-medium">Board / ExCom</p>
            </div>
            <p className="text-xs text-white/55">Items needing Board or Executive Committee attention.</p>
          </div>
        </div>
        <Callout variant="info">
          Items in the SDF MD lane do not go through Awaiting Dispatch — the MD's approval closes them directly because there's no further step up.
        </Callout>
      </Section>

      <Section title="The dispatch journey, start to finish">
        <Steps
          steps={[
            <><RoleBadge role="admin" /> The item is registered in an above-MD lane and worked through In Progress and the review stages as usual.</>,
            <><RoleBadge role="md_admin" /> The SDF MD opens MD Meetings → Review Queue, ticks the item, clicks <span className="text-white">Approve &amp; Close</span>, and writes a closing note. Sign-off is recorded automatically.</>,
            <>Behind the scenes the system spots that this is an above-MD lane and sets the status to <StatusBadge status="AWAITING_DISPATCH" /> instead of Closed. The held-time clock starts.</>,
            <><RoleBadge role="admin" /> The SDF PMO and SDF Board Secretary are emailed. The item shows up on MD Meetings → Awaiting Dispatch.</>,
            <>The PMO opens the row, clicks <span className="text-white">Mark Dispatched</span>, and fills in <span className="text-white">recipient</span>, <span className="text-white">date sent</span>, and an optional note. Status moves to <StatusBadge status="SENT_UPWARD" />.</>,
            <>The DISPATCHED_UPWARD entry is saved with the time taken in working days, and the performance panel updates straight away.</>,
            <>For above-MD lanes, the closure follows automatically — nothing more to do.</>,
          ]}
        />
      </Section>

      <Section title="The held-time chip">
        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 text-cyan-300 shrink-0 mt-0.5" />
          <p className="text-sm text-white/65 leading-relaxed">
            Every row on the Awaiting Dispatch tab shows how long the item has waited since the SDF MD approved it, counted in working days (Mon–Fri only).
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="p-2 rounded-md bg-emerald-500/10 border border-emerald-500/25 text-xs">
            <p className="text-emerald-300 font-semibold mb-1">Green · 0–2 days</p>
            <p className="text-white/55">Within the default target.</p>
          </div>
          <div className="p-2 rounded-md bg-amber-500/10 border border-amber-500/25 text-xs">
            <p className="text-amber-300 font-semibold mb-1">Amber · 3–4 days</p>
            <p className="text-white/55">One or two days late — needs attention.</p>
          </div>
          <div className="p-2 rounded-md bg-red-500/10 border border-red-500/25 text-xs">
            <p className="text-red-300 font-semibold mb-1">Red · 5+ days</p>
            <p className="text-white/55">Significantly overdue.</p>
          </div>
        </div>
        <Callout variant="info">
          The 2-working-day target is the default and appears on the Summary performance panel. Your Super Admin can adjust this in a future release.
        </Callout>
      </Section>

      <Section title="Who can do what">
        <ul className="list-disc list-inside text-xs text-white/55 space-y-1.5 ml-2">
          <li><span className="text-white">View the Awaiting Dispatch tab + PDF:</span> MD Admin and above (read-only), plus the SDF MD.</li>
          <li><span className="text-white">Mark Dispatched:</span> any General Admin or above (the PMO and Board Secretary in practice).</li>
          <li><span className="text-white">See it in the history:</span> the DISPATCHED_UPWARD entry and the status change appear in the item's history for everyone who can see that item.</li>
        </ul>
      </Section>
    </div>
  );
}
